// src/components/billing/PlanGrid.jsx — the plans grid: two global toggles over
// one card per plan family, plus the rows that cannot sit on an axis.
//
// ⛔⛔ A TOGGLE SELECTS A SERVER ROW. It never builds one, never merges two and
// never computes a price. Every figure, button and sentence on a card comes from
// the single `options[]` row that position maps to.
//
// ⛔ TWO FALLBACKS EXIST SO THE LAYOUT CAN NEVER HIDE A BUYABLE PLAN:
//   · a family whose rows COLLIDE in one cell renders FLAT (every row as a card)
//   · a family with no row at the selected cell renders a muted PLACEHOLDER
//     (⚠️ real, not defensive: a row whose id is missing from `stripe_price_map`
//     is hidden per-id, so a half-pushed catalogue yields monthly-without-yearly)
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PlanCard from './PlanCard.jsx';
import PlanAxisToggle from './PlanAxisToggle.jsx';
import { CARD_GRID } from './cardGrid.js';
import { groupPlanOptions, seedPosition, resolvePosition, cellKey } from '../../billing/planGroups.js';
import { COLORS, FONT_SANS, RADII } from '../../constants/theme.js';

export default function PlanGrid({ rows, currentPlanId, busy = false, onAct, billingDetailsTo }) {
  const { t } = useTranslation('billing');
  // ⛔⛔ null = "the user has not chosen". The position in force is DERIVED every
  // render (resolvePosition): seeding the state from `seedPosition(...)` would
  // snapshot the seed, and the page re-reads fresh after every successful change.
  const [choice, setChoice] = useState(null);

  const { families, loose, intervals, workspaces } = groupPlanOptions(rows);
  const seed = seedPosition(rows, currentPlanId);
  const pos = resolvePosition({ choice, seed, intervals, workspaces });

  // ⚠️ Mark the current plan by the TOP-LEVEL plan_id, never by a row's action:
  // a free/special customer's own plan is among the HIDDEN rows and would
  // otherwise never be marked.
  // ⚠️ `billingDetailsTo` rides through the ONE card helper, so the grid rows and
  // the loose rows below it cannot drift apart.
  // ⭐ 2026-10-08 `title`: a GRID card is named after its family (the toggles already say «Annuale» / «3 workspace»,
  // and the server's row names carry "(yearly)" / "Bundle"); loose rows and the flat fallback keep their own name.
  const card = (o, title) => (
    <PlanCard key={o.plan_id} option={o} busy={busy} billingDetailsTo={billingDetailsTo}
      isCurrent={o.plan_id === currentPlanId} onAct={onAct} title={title} />
  );

  return (
    <>
      {pos && (intervals.length > 1 || workspaces.length > 1) && (
        <div data-testid="plan-axes" style={{ display: 'flex', gap: 20, flexWrap: 'wrap', justifyContent: 'center', margin: '0 0 18px' }}>
          {intervals.length > 1 && (
            <PlanAxisToggle
              label={t('interval.label')} disabled={busy} value={pos.interval}
              options={intervals.map((v) => ({
                // ⚠️ An interval the FE has no wording for renders RAW.
                value: v, label: t(`interval.${v}`, { defaultValue: v }),
              }))}
              onChange={(v) => setChoice((c) => ({ ...c, interval: v }))} />
          )}
          {workspaces.length > 1 && (
            <PlanAxisToggle
              label={t('axis.workspaces')} disabled={busy} value={pos.workspaces}
              options={workspaces.map((n) => ({
                value: n, label: t('axis.workspacesOption', { count: n }),
              }))}
              onChange={(v) => setChoice((c) => ({ ...c, workspaces: v }))} />
          )}
        </div>
      )}

      <div data-testid="card-grid" style={CARD_GRID}>
        {families.map((f) => {
          if (f.collides || !pos) return f.rows.map((o) => card(o));
          const row = f.byCell.get(cellKey(pos.interval, pos.workspaces));
          // ⭐ The placeholder is named after the family's FIRST row in server
          // order — its cheapest, which is the clean tier name.
          return row
            ? card(row, f.rows[0].display_name)
            : <MissingCell key={f.family} t={t} name={f.rows[0].display_name} />;
        })}
      </div>

      {loose.length > 0 && (
        <div data-testid="card-grid" style={{ ...CARD_GRID, marginTop: 16 }}>{loose.map((o) => card(o))}</div>
      )}
    </>
  );
}

function MissingCell({ t, name }) {
  return (
    <div style={{
      background: COLORS.surface, border: `1px dashed ${COLORS.border}`,
      borderRadius: RADII.control, padding: '18px 20px',
      fontFamily: FONT_SANS, opacity: 0.6,
    }}>
      {/* Named like a plan card (PlanCard: 22px, centred). */}
      <h3 style={{ fontSize: 22, fontWeight: 700, margin: 0, textAlign: 'center' }}>{name}</h3>
      <p style={{ fontSize: 12, color: COLORS.textSecondary, margin: '10px 0 0' }}>
        {t('plan.notInThisCombination')}
      </p>
    </div>
  );
}
